// ONE list of release notes. The navbar dropdown and the Updates page both read from it,
// so the red "new update" dot and the page always agree.
// To announce a release: add it at the TOP and give it a new version.
//
// item.type: "feature" | "improvement" | "performance" | "design" | "fix"
// link (optional): "Open ..." button. The first entry whose roles match the user is shown.
export const RELEASES = [
  {
    version: "v1.1.0",
    date: "October 14, 2026",
    title: "New Reports page for managers",
    summary:
      "Reports are now easy to read. See your clients, leads, enquiries, production jobs and warehouse stock as simple charts and tables, with short notes that explain every number.",
    link: [
      {
        roles: ["ROLE_SALES-MANAGER"],
        to: "/reports/overview",
        label: "Open Reports",
      },
      {
        roles: ["ROLE_PROD-MANAGER"],
        to: "/reports/operation",
        label: "Open Reports",
      },
      {
        roles: ["ROLE_INV-MANAGER", "ROLE_INVENTORY-MANAGER"],
        to: "/reports/warehouse",
        label: "Open Reports",
      },
    ],
    items: [
      {
        type: "feature",
        heading: "Sales overview",
        body: "A quick look at your sales: clients, leads, enquiries and the money won, with top performers, top clients and recent activity.",
      },
      {
        type: "feature",
        heading: "Who has the most clients",
        body: "See which person looks after the most clients, and how many of them are new, busy or quiet.",
      },
      {
        type: "feature",
        heading: "Newly added clients",
        body: "A list of the newest clients, with who owns them and where they came from. You can also show only new or unassigned clients in the table.",
      },
      {
        type: "feature",
        heading: "Leads and Enquiries reports",
        body: "See how many leads became clients, which sources work best, why leads are lost, and which enquiries are stuck, late or about to close.",
      },
      {
        type: "feature",
        heading: "Operations report",
        body: "See production jobs that are finished, waiting or failed quality checks, and how much each employee has done.",
      },
      {
        type: "feature",
        heading: "Completed jobs report",
        body: "See every finished production job: what it was, who did it, which order and client it was for, how long it took, and whether it was finished on time.",
      },
      {
        type: "feature",
        heading: "Warehouse stock report",
        body: "See how much stock you have, what came in and went out, how full each warehouse is, and which items are running low.",
      },
      {
        type: "feature",
        heading: "Charts and Table views",
        body: "Switch between Charts and Table. Every number you see in a chart is also shown in a table.",
      },
      {
        type: "feature",
        heading: "Click a number to see what is behind it",
        body: "Click any number card to open a pop-up with the list of records that make up that number, for example the four new clients added this month. If the details are already on the page, it scrolls to them and highlights them instead.",
      },
      {
        type: "feature",
        heading: "Switch any chart to a table",
        body: "Every chart now has a small switch in its corner. Flip it to see the same numbers as a table, then flip back.",
      },
      {
        type: "improvement",
        heading: "Click a chart to filter",
        body: "Click a source, industry, status or priority in a chart to see only those records.",
      },
      {
        type: "improvement",
        heading: "Pick any time period",
        body: "Choose today, this week, this month, last month, the last 30 or 90 days, this quarter, this year, or your own dates.",
      },
      {
        type: "improvement",
        heading: "Compare with before",
        body: 'Each number tells you how you are doing compared with the time before, in plain words like "25% more than before".',
      },
      {
        type: "improvement",
        heading: "Numbers that explain themselves",
        body: 'Every card has a short note in simple words, for example "New clients: added this month".',
      },
      {
        type: "improvement",
        heading: "Download and print",
        body: "Download any table as a CSV file, or print the page.",
      },
      {
        type: "improvement",
        heading: "Reports for each role",
        body: "Sales managers see Sales overview, Clients, Leads and Enquiries. Production managers see Operations. Inventory managers see Warehouse stock.",
      },
      {
        type: "performance",
        heading: "Faster reports",
        body: "Numbers are now worked out on the server, so reports open quickly even when you have a lot of data.",
      },
      {
        type: "performance",
        heading: "Large tables, one page at a time",
        body: "Search, sorting and page changes now happen on the server, so big tables stay smooth.",
      },
      {
        type: "design",
        heading: "Compact number cards",
        body: "Each card shows its name, a short note and the number in one tidy row, so more fits on the screen.",
      },
      {
        type: "design",
        heading: "Colour-coded statuses and alerts",
        body: "Approved, pending, rejected, low stock and late items are easy to spot at a glance.",
      },
      {
        type: "fix",
        heading: "Warehouse quantity counted too many times",
        body: "Fixed warehouse totals that counted the same stock again for every movement record.",
      },
      {
        type: "fix",
        heading: "Operation report with empty jobs",
        body: "Fixed a problem where the Operation report could fail when a job had no history yet.",
      },
    ],
  },
];

// Short version used by the navbar dropdown.
export const UPDATES_LIST = RELEASES.map(
  ({ version, date, title, summary, link }) => ({
    version,
    date,
    title,
    description: summary,
    link,
  }),
);

export const LATEST_UPDATE_VERSION = RELEASES[0].version;

export const getUpdateLink = (update, hasRole) =>
  (update.link || []).find((l) => l.roles.some((role) => hasRole(role))) ||
  null;
